import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { useAuth } from "../lib/auth";
import { PageHeader, NBCard, NBButton, NBInput, Avatar, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { MapPin, Battery, RefreshCw, Navigation, Radio, Plus, Trash2, X, Bell, BellOff } from "lucide-react";
import {
  enableBackgroundTracking,
  disableBackgroundTracking,
  isBackgroundTrackingEnabled,
} from "../lib/bgLocation";

export default function Tracker() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [members, setMembers] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [bgEnabled, setBgEnabled] = useState(isBackgroundTrackingEnabled());
  const [bgBusy, setBgBusy] = useState(false);

  // Geofences
  const [zones, setZones] = useState([]);
  const [zoneEvents, setZoneEvents] = useState([]);
  const [zoneOpen, setZoneOpen] = useState(false);
  const [zoneForm, setZoneForm] = useState({ name: "", icon: "📍", radius_m: 200, lat: "", lng: "" });

  const load = async () => {
    const [l, m, z, ev] = await Promise.all([
      api.get("/api/locations"),
      api.get("/api/members"),
      api.get("/api/geofences"),
      api.get("/api/geofences/events?limit=20"),
    ]);
    setLocations(l.data);
    setMembers(m.data);
    setZones(z.data);
    setZoneEvents(ev.data);
  };
  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);

  const toggleBgTracking = async () => {
    setBgBusy(true);
    try {
      if (bgEnabled) {
        await disableBackgroundTracking();
        setBgEnabled(false);
        toast.success("Background tracking disabled");
      } else {
        const res = await enableBackgroundTracking();
        if (res.ok) {
          setBgEnabled(true);
          if (res.periodic_sync) {
            toast.success("Background tracking enabled (15 min sync)");
          } else {
            toast.success("Background tracking enabled while app is open");
          }
          load();
        } else if (res.reason === "permission-denied") {
          toast.error("Location permission denied — enable it in browser settings");
        } else {
          toast.error("Could not enable background tracking");
        }
      }
    } finally {
      setBgBusy(false);
    }
  };

  const shareLocation = async () => {
    setUpdating(true);
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      setUpdating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/api/locations", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            address: "",
            battery: 100,
          });
          toast.success("Location shared!");
          load();
        } catch { toast.error("Failed"); }
        setUpdating(false);
      },
      (err) => {
        toast.error("Permission denied. Using demo location.");
        // Fall back to demo
        api.post("/api/locations", { lat: 40.7128, lng: -74.0060, address: "New York (demo)", battery: 85 }).then(load);
        setUpdating(false);
      }
    );
  };

  const memberById = (id) => members.find((m) => m.id === id);

  const useCurrentForZone = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => setZoneForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })),
      () => toast.error("Could not read your location"),
      { enableHighAccuracy: false, timeout: 10000 },
    );
  };

  const submitZone = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/geofences", {
        name: zoneForm.name,
        icon: zoneForm.icon || "📍",
        lat: parseFloat(zoneForm.lat),
        lng: parseFloat(zoneForm.lng),
        radius_m: Math.max(20, parseInt(zoneForm.radius_m, 10) || 200),
      });
      toast.success("Zone added");
      setZoneOpen(false);
      setZoneForm({ name: "", icon: "📍", radius_m: 200, lat: "", lng: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const removeZone = async (id) => {
    if (!window.confirm("Delete this zone? Existing events stay.")) return;
    await api.delete(`/api/geofences/${id}`);
    load();
  };

  const fmtRel = (iso) => {
    const d = new Date(iso);
    const m = Math.floor((Date.now() - d.getTime()) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    return `${Math.floor(m / 1440)}d ago`;
  };

  // Build a simple map view
  const center = locations.length > 0
    ? { lat: locations.reduce((a, b) => a + b.lat, 0) / locations.length, lng: locations.reduce((a, b) => a + b.lng, 0) / locations.length }
    : { lat: 40.7128, lng: -74.0060 };

  return (
    <div data-testid="tracker-page">
      <PageHeader
        title="Family Tracker"
        subtitle="See where everyone is, in real time."
        color="#A8BFD1"
        action={
          <div className="flex gap-2 flex-wrap">
            <NBButton
              testId="toggle-bg-tracking"
              color={bgEnabled ? "#B8CFA9" : undefined}
              onClick={toggleBgTracking}
              disabled={bgBusy}
            >
              <Radio strokeWidth={2.4} className={`w-4 h-4 inline mr-1 ${bgEnabled ? "animate-pulse" : ""}`} />
              {bgEnabled ? "Background ON" : "Enable background"}
            </NBButton>
            <NBButton testId="share-location-btn" color="#A8BFD1" onClick={shareLocation} disabled={updating}>
              <Navigation strokeWidth={2.4} className={`w-4 h-4 inline mr-1 ${updating ? "animate-pulse" : ""}`} />
              Share now
            </NBButton>
          </div>
        }
      />

      {/* Map placeholder - using OpenStreetMap iframe */}
      <NBCard hover={false} className="!p-0 overflow-hidden mb-6">
        <div className="aspect-video w-full bg-[#B8CFA9] relative">
          <iframe
            title="Family Map"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${center.lng - 0.05},${center.lat - 0.05},${center.lng + 0.05},${center.lat + 0.05}&layer=mapnik&marker=${center.lat},${center.lng}`}
            className="w-full h-full"
            data-testid="family-map"
          />
        </div>
      </NBCard>

      {locations.length === 0 ? (
        <EmptyState
          title="No one's sharing yet"
          subtitle="Tap 'Share My Location' to start. Family members will appear here in real-time."
          action={<NBButton color="#A8BFD1" onClick={shareLocation}>Share my location</NBButton>}
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((loc) => {
            const m = memberById(loc.member_id);
            const updated = new Date(loc.updated_at);
            const minsAgo = Math.floor((Date.now() - updated.getTime()) / 60000);
            return (
              <NBCard key={loc.member_id} data-testid={`location-${loc.member_id}`}>
                <div className="flex items-center gap-3">
                  <Avatar name={m?.name || "?"} color={m?.avatar_color || "#E8B96B"} size={48} />
                  <div className="flex-1">
                    <h4 className="text-lg font-black" style={{ fontFamily: "var(--hh-display)" }}>{m?.name || "Unknown"}</h4>
                    <p className="text-xs font-bold text-neutral-600">{minsAgo < 1 ? "Just now" : `${minsAgo}m ago`}</p>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <p className="text-sm flex items-center gap-2"><MapPin strokeWidth={2.5} className="w-4 h-4 shrink-0" /> {loc.address || `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`}</p>
                  {loc.battery !== undefined && <p className="text-sm flex items-center gap-2"><Battery strokeWidth={2.5} className="w-4 h-4 shrink-0" /> {loc.battery}%</p>}
                </div>
                <a
                  href={`https://www.openstreetmap.org/?mlat=${loc.lat}&mlon=${loc.lng}#map=15/${loc.lat}/${loc.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="nb-btn bg-[#B8CFA9] mt-3 inline-block px-3 py-1.5 text-xs"
                >
                  View on map
                </a>
              </NBCard>
            );
          })}
        </div>
      )}

      {/* Geofence zones */}
      <div className="mt-10" data-testid="geofences-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>Zones</h3>
            <p className="text-sm" style={{ color: "var(--hh-ink-soft)" }}>Get notified when family arrives or leaves a place.</p>
          </div>
          <NBButton testId="add-zone-btn" color="#E9B5A0" onClick={() => setZoneOpen(true)}>
            <Plus strokeWidth={2.4} className="w-4 h-4 inline mr-1" />Zone
          </NBButton>
        </div>

        {zones.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--hh-ink-soft)" }}>No zones yet. Tap "+ Zone" to add Home, School, Office, etc.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {zones.map((z) => (
              <div key={z.id} className="nb-card !p-4 flex items-start gap-3" data-testid={`zone-${z.id}`}>
                <div className="text-3xl shrink-0">{z.icon || "📍"}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-lg" style={{ fontFamily: "var(--hh-display)" }}>{z.name}</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--hh-ink-soft)" }}>
                    {z.lat.toFixed(4)}, {z.lng.toFixed(4)} · {z.radius_m}m radius
                  </div>
                  <div className="flex gap-1 mt-2">
                    {z.notify_on_arrive && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--hh-sage-soft)" }}>arrive</span>}
                    {z.notify_on_leave && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--hh-peach)" }}>leave</span>}
                    {!z.notify_on_arrive && !z.notify_on_leave && <span className="text-[10px] flex items-center gap-1" style={{ color: "var(--hh-ink-soft)" }}><BellOff strokeWidth={2.4} className="w-3 h-3" /> silent</span>}
                  </div>
                </div>
                <button onClick={() => removeZone(z.id)} className="nb-btn p-1.5" data-testid={`delete-zone-${z.id}`}>
                  <Trash2 strokeWidth={2.4} className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {zoneEvents.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--hh-ink-soft)" }}>Recent activity</h4>
            <ul className="space-y-2" data-testid="zone-events-list">
              {zoneEvents.slice(0, 8).map((ev) => (
                <li key={ev.id} className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)" }}>
                  <Bell strokeWidth={2.2} className="w-4 h-4" style={{ color: ev.kind === "arrive" ? "var(--hh-sage)" : "var(--hh-terracotta)" }} />
                  <span className="text-sm flex-1">
                    <strong>{ev.member_name}</strong> {ev.kind === "arrive" ? "arrived at" : "left"} <strong>{ev.zone_name}</strong>
                  </span>
                  <span className="text-xs" style={{ color: "var(--hh-ink-soft)" }}>{fmtRel(ev.at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {zoneOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setZoneOpen(false)} data-testid="zone-modal">
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>New zone</h2>
              <button onClick={() => setZoneOpen(false)} className="nb-btn p-1.5" data-testid="close-zone-modal"><X strokeWidth={2.4} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitZone} className="space-y-3">
              <div className="flex gap-3">
                <NBInput testId="zone-icon" maxLength={4} className="!w-20 text-center text-2xl" placeholder="📍"
                  value={zoneForm.icon} onChange={(e) => setZoneForm({ ...zoneForm, icon: e.target.value })} />
                <NBInput testId="zone-name" required placeholder="Home, School, Office…"
                  value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} className="flex-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <NBInput testId="zone-lat" required type="number" step="0.000001" placeholder="Latitude" value={zoneForm.lat} onChange={(e) => setZoneForm({ ...zoneForm, lat: e.target.value })} />
                <NBInput testId="zone-lng" required type="number" step="0.000001" placeholder="Longitude" value={zoneForm.lng} onChange={(e) => setZoneForm({ ...zoneForm, lng: e.target.value })} />
              </div>
              <NBButton type="button" onClick={useCurrentForZone} className="w-full" testId="zone-use-current">
                <Navigation strokeWidth={2.4} className="w-4 h-4 inline mr-1" />Use my current location
              </NBButton>
              <div>
                <label className="text-[10px] uppercase tracking-widest mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Radius (meters)</label>
                <NBInput testId="zone-radius" type="number" min="20" max="5000" step="10" value={zoneForm.radius_m} onChange={(e) => setZoneForm({ ...zoneForm, radius_m: e.target.value })} />
              </div>
              <NBButton testId="submit-zone" type="submit" color="#E9B5A0" className="w-full">Save zone</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
